import { useState } from "react";
import { useNavigate } from "react-router-dom";
import { api } from "@/lib/config";
import { toast } from "sonner";
import { ArrowRight, ArrowLeft, Check, Users, Calendar, DollarSign, Heart, Send } from "lucide-react";

const INTERESTS = [
  { key: "serengeti", label: "Serengeti & the Migration", icon: "🦁" },
  { key: "ngorongoro", label: "Ngorongoro Crater", icon: "🌋" },
  { key: "tarangire", label: "Tarangire Elephants", icon: "🐘" },
  { key: "zanzibar", label: "Zanzibar Beach", icon: "🏖️" },
  { key: "culture", label: "Maasai & Culture", icon: "🥁" },
  { key: "honeymoon", label: "Honeymoon", icon: "💛" },
  { key: "family", label: "Family-friendly", icon: "👨‍👩‍👧" },
  { key: "photography", label: "Photography", icon: "📷" },
];

const BUDGETS = [
  { key: "under-2k", label: "Under $2,000 / person" },
  { key: "2k-4k", label: "$2,000 – $4,000 / person" },
  { key: "4k-6k", label: "$4,000 – $6,000 / person" },
  { key: "6k-10k", label: "$6,000 – $10,000 / person" },
  { key: "10k-plus", label: "$10,000+ / person" },
  { key: "not-sure", label: "Not sure yet — advise me" },
];

const MONTHS = ["Flexible", "Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

const STEPS = ["Interests", "Trip Shape", "Budget", "Contact"];

export default function CustomTripPage() {
  const navigate = useNavigate();
  const [step, setStep] = useState(0);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    interests: [],
    party_size: 2,
    trip_length_days: 10,
    preferred_month: "Flexible",
    budget_per_person: "",
    full_name: "",
    email: "",
    phone: "",
    notes: "",
  });

  const toggleInterest = (key) => {
    setForm((f) => ({
      ...f,
      interests: f.interests.includes(key)
        ? f.interests.filter((i) => i !== key)
        : [...f.interests, key],
    }));
  };

  const canNext = () => {
    if (step === 0) return form.interests.length > 0;
    if (step === 1) return form.party_size >= 1 && form.trip_length_days >= 2;
    if (step === 2) return !!form.budget_per_person;
    if (step === 3) return form.full_name.trim().length >= 2 && form.email.includes("@") && form.phone.trim().length >= 5;
    return false;
  };

  const submit = async () => {
    setSubmitting(true);
    try {
      const res = await api.post("/custom-trip", form);
      toast.success(`Received! Lead reference ${res.data.lead.lead_reference}`);
      navigate(`/custom-trip-success?ref=${res.data.lead.lead_reference}&email=${encodeURIComponent(form.email)}`);
    } catch (err) {
      const msg = err?.response?.data?.detail?.[0]?.msg || err?.response?.data?.detail || "Something went wrong. Try WhatsApp instead.";
      toast.error(typeof msg === "string" ? msg : "Please check your entries.");
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="custom-trip-page" className="pt-36 md:pt-40 pb-24 bg-[#FAF6F0] min-h-screen">
      <div className="max-w-4xl mx-auto px-6 md:px-12">
        <div className="text-center max-w-3xl mx-auto">
          <p className="overline">custom trip</p>
          <h1 className="font-display text-5xl md:text-7xl italic font-light mt-4 leading-tight">
            Design Your Perfect Tanzania Trip
          </h1>
          <p className="mt-5 text-[#23170E]/65 text-lg">
            Four quick questions. We'll come back with two or three hand-drawn itineraries — always personal, never templated.
          </p>
        </div>

        {/* Progress */}
        <div className="mt-14 flex items-center justify-center gap-3">
          {STEPS.map((label, i) => (
            <div key={label} className="flex items-center gap-3">
              <div
                data-testid={`wizard-step-${i}`}
                className={`w-10 h-10 rounded-full flex items-center justify-center text-sm font-semibold transition-all ${
                  step === i
                    ? "bg-[#A6521B] text-white scale-110 shadow-lg shadow-[#A6521B]/30"
                    : step > i
                    ? "bg-[#A6521B]/80 text-white"
                    : "bg-[#F1E8DC] text-[#23170E]/45"
                }`}
              >
                {step > i ? <Check size={16} /> : i + 1}
              </div>
              {i < STEPS.length - 1 && (
                <div className={`h-px w-8 md:w-16 transition-colors ${step > i ? "bg-[#A6521B]" : "bg-[#23170E]/15"}`} />
              )}
            </div>
          ))}
        </div>
        <div className="mt-4 text-center text-xs uppercase tracking-[0.22em] text-[#A6521B]">
          Step {step + 1} of {STEPS.length} · {STEPS[step]}
        </div>

        {/* Card */}
        <div className="mt-10 bg-[#FFFFFF] rounded-3xl p-8 md:p-12 border border-[#E2D5C3] fade-up" key={step}>
          {step === 0 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <Heart className="text-[#A6521B]" size={22} />
                <h2 className="font-marcellus text-2xl md:text-3xl">What excites you most?</h2>
              </div>
              <p className="text-sm text-[#23170E]/60 mb-8">Pick as many as you like — this shapes your itinerary.</p>
              <div className="grid sm:grid-cols-2 md:grid-cols-4 gap-3">
                {INTERESTS.map((i) => {
                  const on = form.interests.includes(i.key);
                  return (
                    <button
                      key={i.key}
                      type="button"
                      data-testid={`interest-${i.key}`}
                      onClick={() => toggleInterest(i.key)}
                      className={`p-5 rounded-2xl border-2 text-left transition-all ${
                        on
                          ? "border-[#A6521B] bg-[#A6521B]/8"
                          : "border-[#E2D5C3] bg-[#FAF6F0]/40 hover:border-[#23170E]/30"
                      }`}
                    >
                      <div className="text-3xl mb-2">{i.icon}</div>
                      <div className="font-marcellus text-base leading-tight">{i.label}</div>
                    </button>
                  );
                })}
              </div>
            </div>
          )}

          {step === 1 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Users className="text-[#A6521B]" size={22} />
                <h2 className="font-marcellus text-2xl md:text-3xl">Who's going & how long?</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-8">
                <div>
                  <label className="overline">Number of travelers</label>
                  <div className="mt-4 flex items-center gap-4">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, party_size: Math.max(1, f.party_size - 1) }))} data-testid="party-minus" className="w-12 h-12 rounded-full border-2 border-[#BCA993] hover:border-[#A6521B] text-2xl font-light">−</button>
                    <span data-testid="party-count" className="font-marcellus text-5xl w-16 text-center">{form.party_size}</span>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, party_size: Math.min(30, f.party_size + 1) }))} data-testid="party-plus" className="w-12 h-12 rounded-full border-2 border-[#BCA993] hover:border-[#A6521B] text-2xl font-light">+</button>
                  </div>
                </div>

                <div>
                  <label className="overline">Trip length (days)</label>
                  <div className="mt-4 flex items-center gap-4">
                    <button type="button" onClick={() => setForm((f) => ({ ...f, trip_length_days: Math.max(2, f.trip_length_days - 1) }))} data-testid="days-minus" className="w-12 h-12 rounded-full border-2 border-[#BCA993] hover:border-[#A6521B] text-2xl font-light">−</button>
                    <span data-testid="days-count" className="font-marcellus text-5xl w-16 text-center">{form.trip_length_days}</span>
                    <button type="button" onClick={() => setForm((f) => ({ ...f, trip_length_days: Math.min(60, f.trip_length_days + 1) }))} data-testid="days-plus" className="w-12 h-12 rounded-full border-2 border-[#BCA993] hover:border-[#A6521B] text-2xl font-light">+</button>
                  </div>
                </div>
              </div>

              <div className="mt-10">
                <label className="overline flex items-center gap-2"><Calendar size={14} /> Preferred month</label>
                <div className="mt-4 flex flex-wrap gap-2">
                  {MONTHS.map((m) => (
                    <button
                      key={m}
                      type="button"
                      data-testid={`month-${m.toLowerCase()}`}
                      onClick={() => setForm((f) => ({ ...f, preferred_month: m }))}
                      className={`px-4 py-2 rounded-full text-sm transition-colors ${
                        form.preferred_month === m
                          ? "bg-[#A6521B] text-white"
                          : "bg-[#FAF6F0]/50 text-[#23170E] hover:bg-[#23170E]/8"
                      }`}
                    >
                      {m}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          )}

          {step === 2 && (
            <div>
              <div className="flex items-center gap-3 mb-2">
                <DollarSign className="text-[#A6521B]" size={22} />
                <h2 className="font-marcellus text-2xl md:text-3xl">What's your budget per person?</h2>
              </div>
              <p className="text-sm text-[#23170E]/60 mb-8">Excluding international flights. We'll design within — never over.</p>
              <div className="grid sm:grid-cols-2 gap-3">
                {BUDGETS.map((b) => (
                  <button
                    key={b.key}
                    type="button"
                    data-testid={`budget-${b.key}`}
                    onClick={() => setForm((f) => ({ ...f, budget_per_person: b.key }))}
                    className={`p-5 rounded-2xl border-2 text-left transition-all ${
                      form.budget_per_person === b.key
                        ? "border-[#A6521B] bg-[#A6521B]/8"
                        : "border-[#E2D5C3] bg-[#FAF6F0]/40 hover:border-[#23170E]/30"
                    }`}
                  >
                    <div className="font-marcellus text-lg">{b.label}</div>
                  </button>
                ))}
              </div>
            </div>
          )}

          {step === 3 && (
            <div>
              <div className="flex items-center gap-3 mb-6">
                <Send className="text-[#A6521B]" size={22} />
                <h2 className="font-marcellus text-2xl md:text-3xl">How can we reach you?</h2>
              </div>

              <div className="grid sm:grid-cols-2 gap-5">
                <Field label="Full name" required>
                  <input data-testid="ct-name" value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} className="ctinput" />
                </Field>
                <Field label="Email" required>
                  <input data-testid="ct-email" type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="ctinput" />
                </Field>
                <Field label="Phone / WhatsApp" required>
                  <input data-testid="ct-phone" type="tel" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="ctinput" />
                </Field>
              </div>

              <div className="mt-5">
                <Field label="Anything else we should know? (dietary, celebrations, must-see, must-avoid…)">
                  <textarea data-testid="ct-notes" rows={4} value={form.notes} onChange={(e) => setForm({ ...form, notes: e.target.value })} className="ctinput resize-none" />
                </Field>
              </div>

              <div className="mt-8 p-5 rounded-2xl bg-[#FAF6F0]/70 border border-[#E2D5C3] text-sm text-[#23170E]/75">
                <p className="font-marcellus text-lg text-[#23170E]">Quick recap</p>
                <ul className="mt-3 space-y-1.5">
                  <li>• Interests: <strong>{form.interests.map((i) => INTERESTS.find((x) => x.key === i)?.label).filter(Boolean).join(", ") || "—"}</strong></li>
                  <li>• Party: <strong>{form.party_size} travelers</strong> · <strong>{form.trip_length_days} days</strong> · <strong>{form.preferred_month}</strong></li>
                  <li>• Budget: <strong>{BUDGETS.find((b) => b.key === form.budget_per_person)?.label || "—"}</strong></li>
                </ul>
              </div>
            </div>
          )}
        </div>

        {/* Nav buttons */}
        <div className="mt-8 flex justify-between items-center">
          <button
            type="button"
            data-testid="wizard-back"
            disabled={step === 0}
            onClick={() => setStep((s) => Math.max(0, s - 1))}
            className={`btn-pill ${step === 0 ? "opacity-30 pointer-events-none" : "btn-pill-outline"}`}
          >
            <ArrowLeft size={16} /> Back
          </button>

          {step < STEPS.length - 1 ? (
            <button
              type="button"
              data-testid="wizard-next"
              disabled={!canNext()}
              onClick={() => setStep((s) => s + 1)}
              className={`btn-pill btn-pill-primary ${!canNext() ? "opacity-50 pointer-events-none" : ""}`}
            >
              Continue <ArrowRight size={16} />
            </button>
          ) : (
            <button
              type="button"
              data-testid="wizard-submit"
              disabled={!canNext() || submitting}
              onClick={submit}
              className={`btn-pill btn-pill-primary ${!canNext() || submitting ? "opacity-50 pointer-events-none" : ""}`}
            >
              {submitting ? "Sending…" : "Send My Request"} <Send size={14} />
            </button>
          )}
        </div>
      </div>

      <style>{`
        .ctinput { width: 100%; background: #FAF6F0; border: 1px solid rgba(58, 39, 24, 0.18); border-radius: 9999px; padding: 12px 20px; color: #23170E; outline: none; transition: border-color 200ms; font-family: 'Manrope', sans-serif; font-size: 0.95rem; }
        .ctinput:focus { border-color: #A6521B; }
        textarea.ctinput { border-radius: 24px; padding: 14px 20px; }
      `}</style>
    </div>
  );
}

function Field({ label, required, children }) {
  return (
    <label className="block">
      <span className="overline">{label}{required && " *"}</span>
      <div className="mt-3">{children}</div>
    </label>
  );
}
